import { AutoFormFields } from 'lib/LegacyForm';
import { Option } from 'lib/types';
import {
    ContentModule,
    ContentModuleType,
    displayToMarketingHeight,
    EHeightOption,
    IDisplay,
    IItemContext
} from '../models/ContentModule';
import { createMarketingItemForm } from './createMarketingItemForm';
import { createModuleBasicForm, transformBasicFormFieldToModuleFields } from './createModuleBasicForm';
import { getMarketingModuleForm } from './MarketingModuleItem';

// mapping between Option[] and string[] formats
function mapOptionsToValues(options: (Option | string)[] | undefined): string[] {
    return Array.isArray(options) ? options.map(opt => (typeof opt === 'string' ? opt : opt.value)) : [];
}

function mapIdsToOptions(ids: string[], options: Option[]): Option[] {
    return ids.map(id => {
        const option = options?.find(opt => opt.value === id);
        return option ?? { value: id, label: id };
    });
}

export interface MarketingModuleAdditionalData {
    display?: IDisplay;
    locationOptions?: Option[];
    segmentOptions?: Option[];
    selectedSegmentIds?: Option[];
    selectedExcludingSegmentIds?: Option[];
}

function getModuleContext(formData: { [key: string]: any }): Omit<IItemContext, 'visibleTo' | 'visibleFrom'> {
    const locationIds = mapOptionsToValues(formData?.locationIds);
    const segmentIds = mapOptionsToValues(formData?.segmentIds);
    const excludingSegmentIds = mapOptionsToValues(formData?.excludingSegmentIds);

    const authenticated = formData?.authenticated ?? true;
    const unauthenticated = formData?.unauthenticated ?? true;

    const context: Omit<IItemContext, 'visibleTo' | 'visibleFrom'> = {
        authenticated,
        unauthenticated
    };

    if (locationIds.length > 0) {
        context.locationIds = locationIds;
    } else if (formData?.locationIds !== undefined) {
        context.locationIds = undefined;
    }

    if (segmentIds.length > 0) {
        context.segmentIds = segmentIds;
    } else if (formData?.segmentIds !== undefined) {
        context.segmentIds = undefined;
    }

    if (excludingSegmentIds.length > 0) {
        context.excludingSegmentIds = excludingSegmentIds;
    } else if (formData?.excludingSegmentIds !== undefined) {
        context.excludingSegmentIds = undefined;
    }

    return context;
}

const MARKETING_SHAPE_TO_LABEL: Record<EHeightOption, string> = {
    [EHeightOption.SINGLE_HEIGHT_RECTANGLE]: 'Single Height Rectangle (3:1)',
    [EHeightOption.DOUBLE_HEIGHT_RECTANGLE]: 'Double Height Rectangle (3:2)',
    [EHeightOption.SMALL_SQUARE]: 'Small Square (1:1)',
    [EHeightOption.LARGE_SQUARE]: 'Large Square (1:1)'
};

export const createModuleMarketingForm: (
    additionalData?: MarketingModuleAdditionalData
) => AutoFormFields = additionalData => {
    const hasSegmentIds = (additionalData?.selectedSegmentIds?.length ?? 0) > 0;
    const hasExcludingSegmentIds = (additionalData?.selectedExcludingSegmentIds?.length ?? 0) > 0;

    return {
        ...createModuleBasicForm,
        display: {
            label: 'Shape',
            type: 'select',
            options: Object.keys(EHeightOption).map((key: EHeightOption) => ({
                label: MARKETING_SHAPE_TO_LABEL[key],
                value: key
            }))
        },
        contextLabel: {
            type: 'label',
            label: 'Context (Optional)'
        },
        locationIds: {
            label: 'Locations',
            type: 'multiSelect',
            options: additionalData?.locationOptions ?? [],
            description:
                'Choose which locations you would like to display this content to when a user is checked in, or is set as the favourite location. ' +
                'Leaving this empty will show it to all locations.'
        },
        segmentIds: {
            label: 'Show if user is in Audience',
            type: 'multiSelect',
            options: additionalData?.segmentOptions ?? [],
            disabled: hasExcludingSegmentIds,
            description: hasExcludingSegmentIds
                ? 'Cannot show to audiences when hide audiences is set. Clear hide audiences to use this field.'
                : 'Choose which audiences you would like to display this content to. ' +
                  'Leaving this empty will show it to all audiences.'
        },
        excludingSegmentIds: {
            label: 'Hide if user is in Audience',
            type: 'multiSelect',
            options: additionalData?.segmentOptions ?? [],
            disabled: hasSegmentIds,
            description: hasSegmentIds
                ? 'Cannot hide from audiences when show audiences is set. Clear show audiences to use this field.'
                : 'Choose which audiences you would not like to display this module to. ' +
                  'Leaving this empty will show it to all audiences.'
        },
        authenticatedLabel: {
            type: 'label',
            label: 'Show content when user is:'
        },
        authenticated: {
            label: 'Signed In',
            type: 'checkbox',
            defaultValue: true
        },
        unauthenticated: {
            label: 'Signed Out',
            type: 'checkbox',
            defaultValue: true
        },
        items: {
            type: 'sortableField',
            fields: [
                { label: 'Action', width: '40%', key: 'action' },
                { label: 'Reporting Name', width: '45%', key: 'name' }
            ],
            itemForm: createMarketingItemForm,
            FormComponent: getMarketingModuleForm(additionalData && additionalData.display),
            label: 'Marketing messages',
            validation: 'required',
            errors: {
                required: 'This field is required'
            }
        }
    };
};

export function transformModuleToMarketingFormFields(
    module: ContentModule,
    options?: { locationOptions?: Option[]; segmentOptions?: Option[] }
): any {
    const baseFields = {
        ...module,
        secondaryActionTitle: module.secondaryAction ? module.secondaryAction.title : undefined,
        secondaryActionAction: module.secondaryAction ? module.secondaryAction.action : undefined,
        display: displayToMarketingHeight(module.display)
    };

    if (module.type === ContentModuleType.MARKETING) {
        const locationIds = module.context?.locationIds ?? [];
        const segmentIds = module.context?.segmentIds ?? [];
        const excludingSegmentIds = module.context?.excludingSegmentIds ?? [];

        const mappedLocationIds = mapIdsToOptions(locationIds, options?.locationOptions || []);
        const mappedSegmentIds = mapIdsToOptions(segmentIds, options?.segmentOptions || []);
        const mappedExcludingSegmentIds = mapIdsToOptions(excludingSegmentIds, options?.segmentOptions || []);

        return {
            ...baseFields,
            locationIds: mappedLocationIds,
            segmentIds: mappedSegmentIds,
            excludingSegmentIds: mappedExcludingSegmentIds,
            authenticated: module.context?.authenticated ?? true,
            unauthenticated: module.context?.unauthenticated ?? true
        };
    }

    return baseFields;
}

export function transformMarketingFormFieldToModuleFields(
    fieldName: string,
    value: any,
    formData: { [key: string]: any }
): { name: string; value: any }[] {
    if (fieldName === 'secondaryActionTitle' || fieldName === 'secondaryActionAction') {
        return transformBasicFormFieldToModuleFields(fieldName, value, formData);
    }

    switch (fieldName) {
        case 'display':
            return [
                {
                    name: 'display',
                    value: {
                        height: value
                    }
                }
            ];
        case 'locationIds':
        case 'segmentIds':
        case 'excludingSegmentIds':
        case 'authenticated':
        case 'unauthenticated':
            return [{ name: 'context', value: getModuleContext(formData) }];
        default:
            return [{ value, name: fieldName }];
    }
}
